import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setShowClosedCaptions } from 'store/actions/student';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { Box } from '@mui/material';
import ForwardButton from 'components/ForwardButton';
import backButton from 'assets/images/back-button.svg';
import AudioCaptions from 'components/AudioCaptions';
import PlayPauseButton from 'components/PlayPauseButton';
import { handleRoundEnd } from 'store/actions/activity';
import { getRedirectPathname } from 'utils/getUrlParams';
import {
  A11ySettingsPopover,
  ExternalControl
} from '@lwtears/lwt-common-frontend/lib/@a11y/A11ySettings';
import { useLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';
import { applyA11ySettings } from '@lwtears/lwt-common-frontend/lib/@common/util/a11y-util';
import { ClosedCaptionButton } from '@lwtears/lwt-common-frontend/lib/@a11y/A11ySettings/components/ClosedCaptionButton';
import { getAudioVendor } from 'utils/accessibilityUtils';
import { currentAudioSingleton } from 'utils/assetLoader';
import styles from './Footer.module.scss';
import { t } from '../../translations';

const backRedirect = (pathname, appMode, module) => {
  return getRedirectPathname(appMode, pathname, module);
};

const Footer = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    appMode,
    module,
    forwardButton,
    loading,
    isLiteVersion,
    hasAccessibilityMode
  } = useSelector(state => state.ui);
  const { showBackgroundOfAward } = useSelector(state => state.award);
  const { activities, currentRoundIndex, flowComplete } = useSelector(state => state.activity);
  const { showAward } = useSelector(state => state.award);
  const dispatch = useDispatch();
  const paths = ['/pin-recovery', '/app-selection', `/${module}/splash`, '/tester'];
  const [state] = useLwtStore();
  const {
    settings: {
      accessibility: {
        closedCaptionsSettings: { showClosedCaptions, displayCCControl },
        displaySettings: { contentScaling }
      }
    }
  } = state;

  const currentIndexRef = useRef(currentRoundIndex);
  const flowCompleteRef = useRef(flowComplete);
  const { type } = activities[currentIndexRef.current] ?? {};

  const isDidYouKnow = type === 'DidYouKnow';
  const useSimplifiedA11yControls = isLiteVersion && hasAccessibilityMode && isDidYouKnow;

  const [ariaLabel, setAriaLabel] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const showCC = useSelector(state => state.student.showCC);
  const handleSetShowClosedCaptions = value => dispatch(setShowClosedCaptions(value));

  // Poll the current audio instance for play state
  useEffect(() => {
    const interval = setInterval(() => {
      const howl = currentAudioSingleton.get();
      setIsPlaying(howl ? howl.playing() : false);
    }, 300);
    return () => clearInterval(interval);
  }, []);

  const handlePlayPause = () => {
    const howl = currentAudioSingleton.get();
    if (!howl) return;
    if (howl.playing()) {
      howl.pause();
    } else {
      howl.play();
    }
  };

  useEffect(() => {
    currentIndexRef.current = currentRoundIndex;
  }, [currentRoundIndex]);

  useEffect(() => {
    flowCompleteRef.current = flowComplete;
  }, [flowComplete]);

  useEffect(() => {
    if (pathname.includes('reward-screen')) {
      setAriaLabel(t('GoBackAppSelection'));
    }
    if (pathname.includes('activity')) {
      setAriaLabel(t('GoBackARewardScreen'));
    }
  }, [pathname]);

  const canGoBack = !showAward;

  const redirectToNextActivity = () => {
    const { type } = activities[currentIndexRef.current];
    // redirect if activity complete
    if (flowCompleteRef.current) {
      navigate(appMode === 'tester' ? '/tester' : `/${module}/reward-screen`);
    } else {
      navigate(`/${module}/activity/${type}`);
    }
  };

  const endRound = async () => {
    await dispatch(handleRoundEnd());
    process.nextTick(() => redirectToNextActivity());
  };

  if (paths.includes(pathname)) return null;
  if (isLiteVersion && (!showClosedCaptions || !displayCCControl) && !hasAccessibilityMode)
    return null;

  return (
    <CSSTransition
      in={!loading && !showBackgroundOfAward}
      timeout={500}
      mountOnEnter
      unmountOnExit
      classNames={{
        enter: 'fade-enter',
        enterActive: 'fade-enter-active',
        exit: 'fade-exit'
      }}
    >
      <div
        className={[styles.footerContainer, styles[`footerContainer${contentScaling}`]].join(' ')}
      >
        {!isLiteVersion && (
          <div className={styles.backButtonDiv} id="backButtonDiv">
            <Link
              to={backRedirect(pathname, appMode, module)}
              className={[styles.backButton, styles[`backButton${contentScaling}`]].join(' ')}
              role="button"
              aria-label={ariaLabel}
              style={{ pointerEvents: canGoBack ? 'auto' : 'none' }}
              tabIndex="0"
            >
              <img src={backButton} alt="" />
            </Link>
          </div>
        )}
        <Box
          id="ccDiv"
          className={styles.ccDiv}
          {...(useSimplifiedA11yControls && {
            sx: {
              width: '90% !important',
              '@media screen and (max-width: 400px)': { overflow: 'auto' }
            }
          })}
        >
          {((useSimplifiedA11yControls && showCC) || (showClosedCaptions && displayCCControl)) && (
            <AudioCaptions />
          )}
        </Box>
        {hasAccessibilityMode && !useSimplifiedA11yControls && (
          <Box sx={{ marginRight: '80px' }}>
            <A11ySettingsPopover
              theme="light"
              onSubmit={response => applyA11ySettings(response.data, getAudioVendor())}
              externalControls={[ExternalControl.AUDIO_CONTROLS, ExternalControl.CC_BUTTON]}
            />
          </Box>
        )}
        {useSimplifiedA11yControls && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'end',
              gap: '10px',
              marginRight: '40px',
              '@media screen and (max-width: 400px)': {
                gap: '1px',
                marginRight: '2px',
                height: '40px',
                '& button': {
                  height: '40px !important',
                  width: '40px !important',
                  minWidth: '40px !important'
                }
              },
              '@media screen and (max-width: 200px)': {
                gap: '0px',
                marginRight: '0px',
                height: '20px',
                '& button': {
                  height: '20px !important',
                  width: '20px !important',
                  minWidth: '20px !important',
                  fontSize: '4px !important',
                  '.lwt-common-ccButtonText': {
                    fontSize: '4px !important'
                  }
                }
              }
            }}
          >
            <PlayPauseButton isPlaying={isPlaying} onClick={handlePlayPause} />
            <ClosedCaptionButton
              onToggleChange={() => () => handleSetShowClosedCaptions(!showCC)}
              showClosedCaptions={showCC}
            />
          </Box>
        )}
        {!useSimplifiedA11yControls && (
          <div className={styles.forwardButtonDiv}>
            {forwardButton.show && !isLiteVersion && (
              <ForwardButton onClick={endRound} contentScaling={contentScaling} />
            )}
          </div>
        )}
      </div>
    </CSSTransition>
  );
};

export default Footer;
